"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useI18n } from "@/i18n";
import { useLangHref } from "@/i18n/useLangHref";
import {
    hasConsented,
    acceptAll,
    rejectAll,
    subscribe,
} from "@/lib/cookie-consent";

/**
 * 首次访问 / 未做过 Cookie 决策时，页面底部滑入的低打扰横幅。
 * 一旦用户做出选择（接受/拒绝/保存），横幅立即隐藏；刷新后也不再出现，直到 resetConsent()。
 */
export default function CookieBanner() {
    const { t } = useI18n();
    const langHref = useLangHref();
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);

    // 在客户端挂载后再检查，避免 SSR 闪烁
    useEffect(() => {
        setMounted(true);
        if (!hasConsented()) {
            // 轻微延迟让页面先呈现
            const t = setTimeout(() => setOpen(true), 400);
            return () => clearTimeout(t);
        }
        const off = subscribe(() => setOpen(false));
        return off;
    }, []);

    if (!mounted) return null;
    if (!open) return null;

    return (
        <>
            <style>{`
                @keyframes cbSlideUp {
                    from { transform: translateY(100%); opacity: 0; }
                    to   { transform: translateY(0);     opacity: 1; }
                }
            `}</style>
            <div
                role="dialog"
                aria-live="polite"
                style={{
                    position: "fixed",
                    left: 16,
                    right: 16,
                    bottom: 16,
                    maxWidth: 960,
                    margin: "0 auto",
                    background: "#ffffff",
                    borderRadius: 16,
                    boxShadow:
                        "0 18px 48px rgba(17, 24, 39, 0.18), 0 2px 6px rgba(17, 24, 39, 0.06)",
                    border: "1px solid #ECEEEC",
                    padding: "16px 20px",
                    zIndex: 9998,
                    animation: "cbSlideUp 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                {/* 左侧文字 */}
                <div style={{ flex: "1 1 320px", minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#111827",
                            marginBottom: 4,
                        }}
                    >
                        {t("cookie.banner.title")}
                    </div>
                    <div
                        style={{
                            fontSize: 13,
                            lineHeight: 1.55,
                            color: "#6B7280",
                        }}
                    >
                        {t("cookie.banner.desc")}{" "}
                        {t("cookie.banner.managePrefix")}
                        <Link
                            href={langHref("/cookies")}
                            style={{ color: "#2F9E44", fontWeight: 500 }}
                        >
                            {t("cookie.banner.settings")}
                        </Link>
                        {t("cookie.banner.manageSuffix")}
                    </div>
                </div>

                {/* 右侧按钮 */}
                <div
                    style={{
                        display: "flex",
                        gap: 10,
                        flexShrink: 0,
                        flexWrap: "wrap",
                    }}
                >
                    <Link
                        href={langHref("/cookies")}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            height: 38,
                            padding: "0 16px",
                            borderRadius: 10,
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#374151",
                            background: "#F4F6F4",
                            border: "1px solid #E5E7EB",
                            textDecoration: "none",
                            cursor: "pointer",
                        }}
                        onClick={() => setOpen(false)}
                    >
                        {t("cookie.banner.settings")}
                    </Link>
                    <button
                        onClick={() => {
                            rejectAll();
                            setOpen(false);
                        }}
                        style={{
                            height: 38,
                            padding: "0 16px",
                            borderRadius: 10,
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#111827",
                            background: "#ffffff",
                            border: "1px solid #E5E7EB",
                            cursor: "pointer",
                        }}
                    >
                        {t("cookie.banner.necessary")}
                    </button>
                    <button
                        onClick={() => {
                            acceptAll();
                            setOpen(false);
                        }}
                        style={{
                            height: 38,
                            padding: "0 18px",
                            borderRadius: 10,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#ffffff",
                            background: "#2F9E44",
                            border: "none",
                            cursor: "pointer",
                        }}
                    >
                        {t("cookie.banner.accept")}
                    </button>
                </div>
            </div>
        </>
    );
}
